import { Card, IconButton, Tooltip } from '@mui/material'
import SoftBox from 'components/SoftBox'
import SoftButton from 'components/SoftButton'
import SoftTypography from 'components/SoftTypography'
import Footer from 'examples/Footer'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { api } from 'utils/Api'
import SmartTableCard from 'utils/Smart Table/SmartTableCard'
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SoftInput from 'components/SoftInput'
import PropertiesPopupForm from './PropertiesPopupForm'
import DeleteForm from '../Organization/DeleteForm'
import { useHierarchy } from 'context/HierarchyContext'
import { showError } from 'utils/messageAlerts'
import { showSuccess } from 'utils/messageAlerts'
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ConfirmDialog from 'utils/ConfirmDialog'

function Properties() {
    const [allPropertiesData, setAllPropertiesData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupMode, setPopupMode] = useState("create"); // "create" or "edit"
    const {orgCode, orgDetailTab, propertyCode} = useParams();
    const [deletePopupOpen, setDeletePopupOpen] = useState(false);
    const navigate = useNavigate();
    const { orgId, setPropertyId } = useHierarchy();
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        type: null,
        property: null,
    });


    const handlePropertyClick = (property) => {
        setSelectedProperty(property);
        setPropertyId(property.id)
        navigate(`/super-admin/organization/${orgCode}/properties/${propertyCode || property.property_code}`);
    }

    // Data fetching logic
    const fetchPropertiesData = async () => {
        try {
            const response = await api.su_Property.getSuProperties();
            setAllPropertiesData(response.filter(property => property.organization_id === parseInt(orgId)));
        } catch (e) {
            setAllPropertiesData([]);
        }
    }

    useEffect(() => {
        fetchPropertiesData();
    }, []);

    useEffect(() => {
        setFilteredProperties(allPropertiesData);
    }, [allPropertiesData]);

    // Search Properties
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (!value.trim()) {
            setFilteredProperties(allPropertiesData);
            return;
        }
        const filtered = allPropertiesData.filter((property) =>
            property.name.toLowerCase().includes(value.toLowerCase()) 
        );
        setFilteredProperties(filtered);
    };

    const handleEditProperties = (property) => {
        setPopupMode("edit");
        setSelectedProperty(property);
        setIsPopupOpen(true);
    };

    const handleDeleteProperty = async (id) => {
        try {
            await api.su_Property.deleteSuProperty(id);
            showSuccess("Property deleted successfully");
            fetchPropertiesData();
            setDeletePopupOpen(false);
            setSelectedProperty(null);
        } catch (e) {
            
        }
    };

    const handlePropertyActionClick = (property, action) => {
        setConfirmDialog({
            open: true,
            type: action,
            property,
        });
    };

    const handlePropertyActive = async () => {
        const { property, type } = confirmDialog
        const isDelete = type === "delete";
        const propertyId = property.id;
        try {
            if (isDelete) {
                await api.su_Property.hardDeleteProperty(propertyId);
                showSuccess("Property permanently deleted successfully");
            } else {
                await api.su_Property.restoreProperty({
                    property_id: property.id,
                    organization_id: orgId,
                });
                showSuccess("Property restored successfully");
            }

            fetchPropertiesData();
        } catch (e) { }

        setConfirmDialog({ open: false, type: null, property: null });
    };

    return (
        <> <Card>
            <SmartTableCard
                title={<h4>Properties in Organization</h4>}
                headerRight={[<SoftButton
                    key="add-btn"
                    variant="outlined"
                    color="info"
                    size="small"
                onClick={() => {
                    setPopupMode("create");
                    setSelectedProperty(null);
                    setIsPopupOpen(true)}}
                >
                    Add Property
                </SoftButton>,
                <SoftInput
                    key="search-input"
                    placeholder="Search Properties"
                    value={searchTerm}
                    onChange={handleSearch}
                    size="small"
                />,
                ]}
                columns={[
                    { label: "Property", key: "Property" },
                    { label: "Description", key: "Description", align: "center" },
                    { label: "Property Code", key: "PropertyCode", align: "center" },
                    { label: "PinCode", key: "PinCode", align: "center" },
                    { label: "Active", key: "Active", align: "center" },
                    { label: "Actions", key: "Actions", align: "center" },
                ]}
                rows={filteredProperties.map((property) => {
                    const isActive = property.active === true;
                    const isInactive = !isActive;
                    return {
                    Property: (
                        <SoftBox display="flex" alignItems="center">
                            <SoftBox>
                                <SoftTypography
                                    variant="button"
                                    sx={{
                                        cursor: "pointer",
                                        fontWeight: 600
                                    }}
                                    onClick={() => handlePropertyClick(property)}
                                    >
                                    {property.name}
                                </SoftTypography>
                            </SoftBox>
                        </SoftBox>),
                    Description: property.description,
                    PropertyCode: property.property_code,
                    PinCode: property.pin_code,
                    Active: isActive ? (
                        <SoftTypography variant="button" color="success" fontWeight="bold">
                            Yes
                        </SoftTypography>
                    ) : (
                        <SoftBox display="flex" justifyContent="center" gap="6px">
                            {/* Restore */}
                            <Tooltip title="Restore Unit" arrow>
                                <IconButton
                                    onClick={() => handlePropertyActionClick(property, "restore")}
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        color: "#16A34A",
                                        backgroundColor: "#ECFDF5",
                                        "&:hover": {
                                            backgroundColor: "#D1FAE5",
                                            transform: "scale(1.1)",
                                        },
                                    }}
                                >
                                    <RestoreIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>

                            {/* Delete */}
                            <Tooltip title="Delete Permanently" arrow>
                                <IconButton
                                    onClick={() => handlePropertyActionClick(property, "delete")}
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        color: "#DC2626",
                                        backgroundColor: "#FEF2F2",
                                        "&:hover": {
                                            backgroundColor: "#FEE2E2",
                                            transform: "scale(1.1)",
                                        },
                                    }}
                                >
                                    <DeleteForeverIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </SoftBox>
                    ),
                    Actions: (
                        <SoftBox display="flex" justifyContent="center" gap="8px">
                            <IconButton
                                onClick={() => handleEditProperties(property)}
                                disabled={isInactive}
                                sx={{
                                    border: "1px solid #D0D5DD",
                                    backgroundColor: "#F9FAFB",
                                    width: 34,
                                    height: 34,
                                    "& svg": { fontSize: 18 },
                                    color: "#475467",
                                    "&:hover": {
                                        backgroundColor: "#E0F2FE",
                                        borderColor: "#38BDF8",
                                        color: "#0284C7",
                                    },
                                }}
                            >
                                <EditIcon fontSize="sm" />
                            </IconButton>

                            <IconButton
                                onClick={() => {
                                    setSelectedProperty(property);
                                    setDeletePopupOpen(true);
                                }}
                                disabled={isInactive}
                                sx={{
                                    border: "1px solid #FECACA",
                                    backgroundColor: "#FEF2F2",
                                    width: 34,
                                    height: 34,
                                    "& svg": { fontSize: 18 },
                                    color: "#B91C1C",
                                    "&:hover": {
                                        backgroundColor: "#FEE2E2",
                                        borderColor: "#F87171",
                                        color: "#991B1B",
                                    },
                                }}
                            >
                                <DeleteIcon fontSize="sm" />
                            </IconButton>
                        </SoftBox>
                    ),
                }}
            )}
            />

            <ConfirmDialog
                open={confirmDialog.open}
                type={confirmDialog.type}
                title={confirmDialog.type === "delete" ? "Delete Property" : "Restore Property"}
                description={
                    confirmDialog.type === "delete"
                        ? "This will permanently delete the property. This action cannot be undone."
                        : "Are you sure you want to restore this property?"
                }
                confirmText={confirmDialog.type === "delete" ? "Delete Permanently" : "Restore"}
                onClose={() => setConfirmDialog({ open: false, type: null, property: null })}
                onConfirm={handlePropertyActive}
            />

            {isPopupOpen && (
                <PropertiesPopupForm
                    mode={popupMode} 
                    refreshProperties={fetchPropertiesData}          
                    property={selectedProperty}
                    onClose={() => {
                        setIsPopupOpen(false);
                    }}
                />
            )}

            {deletePopupOpen && (
                <DeleteForm
                    data={selectedProperty}
                    onClose={() => setDeletePopupOpen(false)}
                    onDelete={handleDeleteProperty}
                />
            )}


        </Card>
        </>
    )
}
export default Properties